import * as Raven from 'raven-js'

if (process.env.SENTRY_DSN) {
  Raven.config(process.env.SENTRY_DSN).install()
}

import * as React from 'react'
import ReconnectingWebSocket from 'reconnecting-websocket'
import { render } from 'react-dom'
import { throttle } from 'lodash-es'
import { PaneContainer, Pane, PaneHandle } from '@cloudflare/draggable-pane'
import { TEWRequest } from '@cloudflare/cf-edgeworker-http-request-editor'
import { StyleProvider, createRenderer } from '@cloudflare/style-provider'
import { createComponent, combineRules } from '@cloudflare/style-container'
import { FiddleSidebar } from '../../components/FiddleSidebar'
import { FlexPassthroughRule } from '../../components/common'
import { domready } from '../../lib/domready'
import { getRandomBytes } from '../../lib/get-random-bytes'
import { FiddleService, TPreviewProps } from '../../lib/FiddleService'
import { FiddlePreviewArea } from '../../components/FiddlePreviewArea'
import { RUNTIME_BASE_URL } from '../../lib/config'

const PageStyle = createComponent(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  height: '100%',
}))

interface IState {
  value: string
  id?: string
  wranglerSessionId: string
  sessionId: string
  previewUpdateCount: number
  env: 'debug' | 'production'
  apiBaseUrl: string
  editor?: monaco.editor.ICodeEditor
  didMakeFixGCLBRequest: boolean
  hideEditor: boolean
  editorPaneWidth: number
  devToolsPaneHeight: number
  isWarningBannerShown: boolean
  isMacOs: boolean
  fiddleService: FiddleService
  activePreviewTab: string
  rawRequest: TEWRequest
  toJSON?: () => IState
}

const params = new URLSearchParams(window.location.search);

const defaultState: IState = {
  value: `
addEventListener('fetch', event => {
  event.respondWith(handleRequest(event.request))
})

/**
 * Fetch and log a given request object
 * @param {Request} request
 */
async function handleRequest(request) {
  console.log('Got request', request)
  const response = await fetch(request)
  console.log('Got response', response)
  return response
}
  `.trim(),
  apiBaseUrl: RUNTIME_BASE_URL,
  env: 'production',
  didMakeFixGCLBRequest: false,
  wranglerSessionId: params.get('wrangler_session_id'),
  hideEditor: params.has('hide_editor'),
  editorPaneWidth: 400,
  devToolsPaneHeight: 200,
  sessionId: getRandomBytes(),
  previewUpdateCount: 0,
  isWarningBannerShown: !isChrome(),
  activePreviewTab: 'preview',
  isMacOs: window.navigator.userAgent.indexOf('Macintosh') > -1,
  rawRequest: {
    url: 'https://tutorial.cloudflareworkers.com',
    method: 'GET',
    headers: [['Content-Type', 'text/plain']],
  },
  fiddleService: new FiddleService(),
  toJSON() {
    const result = Object.assign({}, this)
    delete result.editor
    delete result.toJSON
    return result
  },
}

const state = { ...defaultState }

type TStateChanger = (handler: (newState: IState) => Partial<IState>) => IState

let changeState: TStateChanger = handler => {
  const { rawRequest, ...result } = handler(state)
  // Allow partial rawRequest.
  // Don't worry about immutability while we're lazy
  Object.assign(state.rawRequest, rawRequest)
  Object.assign(state, result)

  if (result.value && state.editor && state.editor.getValue() !== state.value) {
    state.editor.setValue(result.value)
  }

  return state
}

function isChrome() {
  return !!(window as any).chrome
}

function persistStateToLocalStorage(state: IState) {
  const persistableKeys = {
    editorPaneWidth: true,
    devToolsPaneHeight: true,
    isWarningBannerShown: true,
  }

  const persistableState: Partial<IState> = Object.keys(state)
    .filter(key => key in persistableKeys)
    .reduce(
      (obj, key) => Object.assign(obj, { [key]: state[key as keyof IState] }),
      {},
    )

  localStorage.setItem(
    'cloudflareworkers-state',
    JSON.stringify(persistableState),
  )
}

const throttledPersistStateToLocalStorage = throttle(
  persistStateToLocalStorage,
  1000,
)

async function saveContents(
  value: string,
  url: string,
  state: IState,
  changeState: TStateChanger,
) {
  state.value = value

  const res = await fetch(`${state.apiBaseUrl}/script`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/javascript; charset=UTF-8',
    },
    body: value,
    credentials: 'include',
  })

  const { id } = JSON.parse(await res.text())

  changeState(state => ({
    rawRequest: { url },
    id,
    previewUpdateCount: state.previewUpdateCount + 1,
  }))

  return id
}

async function getScript(
  id: string,
  state: IState,
  changeState: TStateChanger,
) {
  const res = await fetch(`${state.apiBaseUrl}/script/${id}`, {
    credentials: 'include',
  })

  if (res.status !== 200) {
    return saveContents(state.value, state.rawRequest.url, state, changeState)
  }

  const value = await res.text()
  changeState(state => ({ value }))
}

async function makeFixGCLBRequest(state: IState, changeState: TStateChanger) {
  try {
    const success = await state.fiddleService.fixGCLB()
    if (!success) {
      console.error(
        'failed to propagate load balancer cookie; devtools may not work correctly',
      )
    }
  } catch (e) {
    Raven.captureException(e)
  }

  changeState(state => ({ didMakeFixGCLBRequest: true }))
}

function isEditorDirty(state: IState) {
  return state.editor && state.value !== state.editor.getValue()
}

const styleRenderer = createRenderer({
  dev: state.env === 'debug',
})

const PaneShadow = createComponent(
  combineRules(FlexPassthroughRule, () => ({
    boxShadow: '2px 0 2px rgba(0, 0, 0, 0.05)',
    zIndex: 1,
  })),
)

type TAppProps = IState & { changeState: TStateChanger }
type TAppState = {
  isEditorPaneMoving: boolean
  devtoolsIsMoving: boolean
}
class App extends React.Component<TAppProps, TAppState> {
  constructor(props: TAppProps) {
    super(props)

    this.state = {
      isEditorPaneMoving: false,
      devtoolsIsMoving: false,
    }

    if (params.has('wrangler_ws_port') && params.has('wrangler_session_id')) {
      const port = params.get('wrangler_ws_port');
      const ws = new ReconnectingWebSocket('ws://127.0.0.1:' + port);
      ws.onmessage = this.onWranglerMessage;
    }

    this.onEditorPaneMouseDown = this.onEditorPaneMouseDown.bind(this)
    this.onEditorPaneMouseUp = this.onEditorPaneMouseUp.bind(this)
    this.onRequestEditorPaneChange = this.onRequestEditorPaneChange.bind(this)
    this.onRequestDevToolsPaneChange = this.onRequestDevToolsPaneChange.bind(
      this,
    )
    this.onFiddleEditorRequestChange = this.onFiddleEditorRequestChange.bind(
      this,
    )
    this.onEditorMount = this.onEditorMount.bind(this)
    this.onRequestChange = this.onRequestChange.bind(this)
    this.onDevtoolsPaneMouseDown = this.onDevtoolsPaneMouseDown.bind(this)
    this.onDevtoolsPaneMouseUp = this.onDevtoolsPaneMouseUp.bind(this)
    this.onPreviewPaneTabChange = this.onPreviewPaneTabChange.bind(this)
    this.onRequestSubmit = this.onRequestSubmit.bind(this)
  }

  getPreviewId = (props: TPreviewProps) => {
    return this.props.fiddleService.getPreviewId(props)
  }

  getPreviewUrl = (props: TPreviewProps) => {
    return this.props.fiddleService.getPreviewUrl(props)
  }

  runRawRequest = (props: TPreviewProps) => {
    return this.props.fiddleService.runRawRequest(props)
  }

  render() {
    const { props } = this

    return (
      <StyleProvider renderer={styleRenderer}>
        <PageStyle>
          <PaneContainer
            direction="row"
            onRequestMove={this.onRequestEditorPaneChange}
            onMouseDown={this.onEditorPaneMouseDown}
            onMouseUp={this.onEditorPaneMouseUp}
          >
            {!props.hideEditor && 
              <Pane width={`${props.editorPaneWidth}px`}>
                <PaneShadow direction="row">
                  <FiddleSidebar
                    editorValue={props.value}
                    editorDidMount={this.onEditorMount}
                    requestSave={this.onFiddleEditorRequestChange}
                    isMacOs={props.isMacOs}
                    shareUrl={window.location.href}
                    hideOverflow={this.state.isEditorPaneMoving}
                  />
                  <PaneHandle current={props.editorPaneWidth} />
                </PaneShadow>
              </Pane>
            }
            <Pane>
              <FiddlePreviewArea
                preview={{
                  scriptId: this.props.id,
                  sessionId: this.props.sessionId,
                  request: this.props.rawRequest,
                  updateCount: this.props.previewUpdateCount,
                }}
                getPreviewId={this.getPreviewId}
                getPreviewUrl={this.getPreviewUrl}
                runRawRequest={this.runRawRequest}
                activePreviewTab={this.props.activePreviewTab}
                isPreviewPaneLoading={false}
                sidebarIsMoving={false}
                devtoolsIsMoving={this.state.devtoolsIsMoving}
                fiddleDidMakeFixGCLBRequest={this.props.didMakeFixGCLBRequest}
                devToolsPaneHeight={this.props.devToolsPaneHeight}
                pathToMonaco="/ui/staging/vs"
                onRequestChange={this.onRequestChange}
                onRequestSubmit={this.onRequestSubmit}
                onRequestDevToolsPaneChange={this.onRequestDevToolsPaneChange}
                onDevtoolsPaneMouseDown={this.onDevtoolsPaneMouseDown}
                onDevtoolsPaneMouseUp={this.onDevtoolsPaneMouseUp}
                onPreviewPaneTabChange={this.onPreviewPaneTabChange}
              />
            </Pane>
          </PaneContainer>
        </PageStyle>
      </StyleProvider>
    )
  }

  onEditorMount(editor: monaco.editor.ICodeEditor) {
    ;(editor as monaco.editor.IStandaloneCodeEditor).addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_S,
      onKeyboardSave,
      undefined,
    )
    this.props.changeState(state => ({ editor }))
  }
  
  onWranglerMessage = (e: MessageEvent) => {
    const {
      wranglerSessionId,
      changeState,
    } = this.props;
    
    const data = JSON.parse(e.data);

    if(data && data.type == 'LiveReload' && data.sessionId === wranglerSessionId) {
      changeState(state => ({
        id: data.newId,
        previewUpdateCount: state.previewUpdateCount + 1,
      }))
    }
  }

  onFiddleEditorRequestChange() {
    const {
      editor,
      rawRequest: { url },
      changeState,
    } = this.props
    saveContents(editor.getValue(), url, state, changeState)

    if (state.activePreviewTab === 'documentation') {
      changeState(() => ({ activePreviewTab: 'preview' }))
    }
  }

  onRequestDevToolsPaneChange(devToolsPaneHeight: number) {
    const min = 1
    const max = window.innerHeight - 44 // Size of the header (i know this is brittle)
    devToolsPaneHeight = Math.min(max, Math.max(min, devToolsPaneHeight))
    return this.props.changeState(state => ({ devToolsPaneHeight }))
  }

  onRequestEditorPaneChange(editorPaneWidth: number) {
    const min = 200
    const max = window.innerWidth - 100
    editorPaneWidth = Math.min(max, Math.max(min, editorPaneWidth))
    return this.props.changeState(state => ({ editorPaneWidth }))
  }

  onEditorPaneMouseDown() {
    this.setState({ isEditorPaneMoving: true })
  }

  onEditorPaneMouseUp() {
    if (this.props.editor) {
      this.props.editor.layout()
    }

    this.setState({ isEditorPaneMoving: false })
  }

  onRequestChange(rawRequest: Partial<TEWRequest>) {
    this.props.changeState(() => ({ rawRequest }))
  }

  onDevtoolsPaneMouseDown() {
    this.setState({ devtoolsIsMoving: true })
  }

  onDevtoolsPaneMouseUp() {
    this.setState({ devtoolsIsMoving: false })
  }

  onPreviewPaneTabChange(activePreviewTab: string) {
    this.props.changeState(() => ({ activePreviewTab }))
  }

  onRequestSubmit() {
    this.props.changeState(
      ({ fiddleService, previewUpdateCount, rawRequest: { url } }) => {
        const parsed = fiddleService.parsePreviewUrl(url)

        // Canonicalise URL on submit:
        return {
          previewUpdateCount: previewUpdateCount + 1,
          rawRequest: {
            url: `${parsed.protocol}://${parsed.host}/${parsed.path}`,
          },
        }
      },
    )
  }
}

function renderUrl(state: IState) {
  if (state.id && state.rawRequest.url) {
    const hash = `#${state.id || ''}:${state.rawRequest.url}`

    if (window.location.hash !== hash) {
      history.replaceState(state.toJSON(), '', hash)
    }
  }
}

function renderApp(el: HTMLElement) {
  render(<App {...state} changeState={changeState} />, el)
}

const el = document.createElement('div')
el.id = 'app'

if (!state.didMakeFixGCLBRequest) {
  makeFixGCLBRequest(state, changeState)
}

const oldChangeState = changeState
changeState = handler => {
  const { previewUpdateCount } = state
  const result = oldChangeState(handler)
  if (result.previewUpdateCount !== previewUpdateCount) {
    renderUrl(state)
  }
  renderApp(el)
  return result
}

if (window.location.hash.length > 1) {
  const { id, url } = parseHash(window.location.hash)
  changeState(state => ({ id, rawRequest: { url } }))

  getScript(id, state, changeState)
} else {
  saveContents(state.value, state.rawRequest.url, state, changeState)
}

const onKeyboardSave = () => {
  if (state.editor) {
    const value = state.editor.getValue()
    saveContents(value, state.rawRequest.url, state, changeState)
  }

  if (state.activePreviewTab === 'documentation') {
    changeState(() => ({ activePreviewTab: 'preview' }))
  }
}

domready(window, () => {
  renderApp(el)
  document.body.appendChild(el)
})

function parseHash(hash: string) {
  const [id, ...urlParts] = hash.substring(1).split(':')
  return { id, url: urlParts.join(':') }
}

const confirmNavigationMsg =
  'You have unsaved changes. Are you sure you want to go?'
function confirmNavigation() {
  return window.confirm(confirmNavigationMsg)
}

const onHashChange = (e: HashChangeEvent) => {
  if (isEditorDirty(state) && !confirmNavigation()) {
    renderUrl(state)
    return
  }

  const { id, url } = parseHash(window.location.hash)

  if (id && url) {
    if (id !== state.id) {
      getScript(id, state, changeState)
    }

    changeState(state => ({ id, rawRequest: { url } }))
  } else {
    saveContents(
      defaultState.value,
      url || defaultState.rawRequest.url,
      state,
      changeState,
    )
  }
}

const onBeforeUnload = (e: BeforeUnloadEvent) => {
  if (isEditorDirty(state)) {
    return confirmNavigationMsg
  }
}

window.addEventListener('hashchange', onHashChange)
window.onbeforeunload = onBeforeUnload

try {
  const persistableState = JSON.parse(
    localStorage.getItem('cloudflareworkers-state'),
  )

  if (persistableState) {
    changeState(state => persistableState)
  }

  const oldChangeState = changeState
  changeState = handler => {
    const result = oldChangeState(handler)
    throttledPersistStateToLocalStorage(result)
    return result
  }
} catch (e) {
  console.warn('Error reading from localStorage', e)
}



// WEBPACK FOOTER //
// ./src/targets/standalone-site/index.tsx